import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type PropsWithChildren, useLayoutEffect, useState } from "react";

import { useSession, type WorkspaceSession } from "../auth/session";
import { SessionProvider } from "../auth/SessionProvider";
import { ToastProvider } from "../components/feedback/ToastProvider";
import { ApiProblem } from "../lib/api/client";
import { ConnectionStatusProvider } from "../lib/connection/ConnectionStatusProvider";
import { presetOf } from "../lib/theme/presets";
import { useTheme } from "./theme-context";
import { ThemeProvider } from "./ThemeProvider";

export type AppProvidersProps = PropsWithChildren<{
  /** Test seam: start in this session state instead of asking the API. */
  initialSession?: WorkspaceSession;
}>;

export function AppProviders({ children, initialSession }: AppProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // One retry for a network failure or a server error; a refusal
            // (4xx) would only be refused again.
            retry: (failureCount, error) =>
              failureCount < 1 && !(error instanceof ApiProblem && error.status >= 400 && error.status < 500),
            refetchOnWindowFocus: false,
            staleTime: 30_000,
          },
          mutations: {
            retry: false,
          },
        },
      }),
  );

  return (
    <ThemeProvider>
      <ConnectionStatusProvider>
        {/* The session is itself a query, so the client wraps it. */}
        <QueryClientProvider client={queryClient}>
          <SessionProvider initialSession={initialSession}>
            <BusinessTheme />
            <ToastProvider>{children}</ToastProvider>
          </SessionProvider>
        </QueryClientProvider>
      </ConnectionStatusProvider>
    </ThemeProvider>
  );
}

/**
 * Draws the signed-in business's preset (P5-014), and the default for
 * everyone else, the sign-in screen included. A layout effect, so the
 * change lands before the shell's first paint.
 */
function BusinessTheme() {
  const { session } = useSession();
  const { setBrand } = useTheme();
  const key = session.kind === "signed-in" ? session.branding?.preset_key : undefined;
  useLayoutEffect(() => setBrand(presetOf(key)), [key, setBrand]);
  return null;
}
